import { useState, useCallback } from 'react';
import EventLog from '../MoldDetail/EventLog';
import { useChangeLogs } from '../../hooks/useChangeLogs';
import { ChangeLogEntityType } from '../../types/mold';

export interface AuditLogsPanelProps {
  entityType: ChangeLogEntityType;
  entityId: number;
  pageSize?: number;
}

/**
 * Self-contained audit-log panel: owns the page state, drives the scoped
 * GET /change-logs/{entityType}/{entityId} endpoint via useChangeLogs, and
 * renders the EventLog table with a pagination footer.
 *
 * Shared by the Mold and Mold Order detail pages.
 */
const AuditLogsPanel: React.FC<AuditLogsPanelProps> = ({
  entityType,
  entityId,
  pageSize = 10,
}) => {
  const [page, setPage] = useState(0);
  const offset = page * pageSize;

  const { changeLogs, total, loading, error } = useChangeLogs(
    entityType,
    pageSize,
    offset,
    entityId,
  );

  // Changing the page changes `offset`, which useChangeLogs already refetches
  // on — no explicit retry() here (it would fire a duplicate request with the
  // stale offset that could resolve last and clobber the fresh page).
  const handlePageChange = useCallback((next: number) => {
    setPage(Math.max(0, next));
  }, []);

  return (
    <EventLog
      events={changeLogs}
      total={total}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      loading={loading}
      error={error}
    />
  );
};

export default AuditLogsPanel;
